import axios, { AxiosInstance } from 'axios';

import JWTDecode from 'jwt-decode';
import history from '../../Utils/history';
import { Helper } from '../../Utils';
import { routes } from '../../Routing/routes';

export const apiClient = axios.create({
	baseURL: (window as any).API_URL,
	timeout: 100000,
}) as IApiClient;

apiClient.baseURL = (window as any).API_URL;

apiClient.logout = (redirectToHomePage: boolean) => {
	localStorage.removeItem('refresh_token');
	localStorage.removeItem('access_token');
	localStorage.removeItem('user_name');

	sessionStorage.removeItem('user_name');
	sessionStorage.removeItem('refresh_token');

	if (redirectToHomePage) {
		history.push('/');
	}

	apiClient.onLogoff();
};

apiClient.getUserDataFromAccessToken = () => {
	let accessToken = localStorage.getItem('access_token');

	if (accessToken) {
		let decodedToken = JWTDecode(accessToken) as any;

		let userWorkplaceIdClaim = 'userWorkplaceId';
		let userWorkplaceId = decodedToken[userWorkplaceIdClaim];

		let availableFunctionalitiesClaim = 'availableFunctionalities';
		let availableFunctionalities = decodedToken[availableFunctionalitiesClaim];

		let rolesClaim = 'role';
		let roles = decodedToken[rolesClaim];

		let userDataClaim = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/userdata';
		let userdata = decodedToken[userDataClaim];

		let authData = {} as IUserAuthData;

		authData.id = decodedToken.nameid;
		authData.userName = decodedToken.unique_name;
		authData.name = userdata;
		authData.userWorkplaceId = userWorkplaceId;
		authData.availableFunctionalities = availableFunctionalities;
		authData.roles = Array.isArray(roles) ? roles : [roles];

		return authData;
	}
	return null;
};

apiClient.login = async (username, password, rememberMe): Promise<boolean> => {
	try {
		const res = await apiClient.post('login', {
			username: username,
			password: password,
		});

		if (res.data.success === false) {
			return new Promise<boolean>((resolve, reject) => {
				reject(res.data.errors[0].message);
			});
		}
		localStorage.setItem('access_token', res.data.data.accessToken);

		let userData = apiClient.getUserDataFromAccessToken();
		if (userData) {
			apiClient.onLogin(userData!);
		}

		if (rememberMe) {
			localStorage.setItem('refresh_token', res.data.data.refreshToken);
			localStorage.setItem('user_name', userData?.userName ?? '');
			sessionStorage.removeItem('refresh_token');
		} else {
			localStorage.removeItem('refresh_token');
			sessionStorage.setItem('refresh_token', res.data.data.refreshToken);
			sessionStorage.setItem('user_name', userData?.userName ?? '');
		}

		return new Promise<boolean>((resolve, reject) => {
			resolve(true);
		});
	} catch (error) {
		return new Promise<boolean>((resolve, reject) => {
			reject(error);
		});
	}
};

apiClient.resetPassword = async (token, tmpPassword, newPassword): Promise<boolean> => {
	try {
		const response = await apiClient.post('users/reset-password', {
			token,
			newPassword,
			password: tmpPassword,
		});

		if (response.data.success === false) {
			return new Promise<boolean>((_resolve, reject) => {
				reject(response.data.errors[0].errorCode);
			});
		}

		return new Promise<boolean>((resolve) => {
			resolve(true);
		});
	} catch (error) {
		return new Promise<boolean>((_resolve, reject) => {
			reject(error);
		});
	}
};

apiClient.requestPasswordReset = async (email): Promise<boolean> => {
	try {
		const response = await apiClient.post('users/request-password-reset', { email });

		if (response.data.success === false) {
			return new Promise<boolean>((_resolve, reject) => {
				reject(response.data.errors[0].errorCode);
			});
		}

		return new Promise<boolean>((resolve) => {
			resolve(true);
		});
	} catch (error) {
		return new Promise<boolean>((_resolve, reject) => {
			reject(error);
		});
	}
};

apiClient.changePassword = async (password, newPassword): Promise<boolean> => {
	try {
		const response = await apiClient.post('users/change-password', {
			oldPassword: password,
			newPassword,
		});

		if (response.data.success === false) {
			return new Promise<boolean>((_resolve, reject) => {
				reject(response.data.errors[0].errorCode);
			});
		}

		return new Promise<boolean>((resolve) => {
			resolve(true);
		});
	} catch (error) {
		return new Promise<boolean>((_resolve, reject) => {
			reject(error);
		});
	}
};

apiClient.interceptors.request.use(
	function (config) {
		const token = localStorage.getItem('access_token');
		if (token)
			config.headers['Authorization'] = (function () {
				return 'Bearer ' + localStorage.getItem('access_token');
			})();
		config.headers['ClientTimeZone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return config;
	},
	function (error) {
		return Promise.reject(error);
	}
);

// Add a response interceptor
apiClient.interceptors.response.use(
	function (response) {
		// Any status code that lie within the range of 2xx cause this function to trigger
		// Do something with response data
		return response;
	},
	function (error) {
		// Any status codes that falls outside the range of 2xx cause this function to trigger
		// Do something with response error
		return Promise.reject(error);
	}
);

apiClient.interceptors.response.use(
	(response) => {
		// Return a successful response back to the calling service
		return response;
	},
	(error) => {
		let originalRequest = error.response?.config;

		if (error.response?.status === 400) {
			//pass 400 responses to consumers as success
			return new Promise((resolve, reject) => {
				resolve(error.response);
			});
		}

		if (error.response?.status === 403) {
			//pass 403 responses to consumers as success
			return new Promise((resolve, reject) => {
				resolve(error.response);
			});
		}

		if (error.response?.status === 404) {
			//pass 404 responses to consumers as success
			return new Promise((resolve, reject) => {
				resolve(error.response);
				Helper.setLoader(false);
			});
		}

		if (error.response?.status === 405) {
			//pass 405 responses to consumers as success
			return new Promise((resolve, reject) => {
				resolve(error.response);
			});
		}

		if (error.response?.status === 500) {
			//overwrite 500 responses messages to consumers
			return new Promise((resolve, reject) => {
				error.response.data.success = false;
				error.response.data.errors = [];
				error.response.data.errors[0] = { errorCode: 50000, message: '50000' };
				resolve(error.response);
			});
		}

		if (error.response?.status !== 401) {
			// Return any error which is not due to authentication back to the calling service
			return new Promise((resolve, reject) => {
				reject(error);
			});
		}

		if (error.config.url === 'auth/login/refresh') {
			// Logout user if token refresh didn't work

			sessionStorage.removeItem('refresh_token');
			sessionStorage.removeItem('access_token');
			sessionStorage.removeItem('user_name');

			localStorage.removeItem('refresh_token');
			localStorage.removeItem('access_token');
			localStorage.removeItem('user_name');

			history.push('/Login?returnUrl=' + window.location.href);

			return new Promise((_resolve, reject) => {
				reject(error);
			});
		}

		// Try request again with new token
		try {
			var refreshToken = localStorage.getItem('refresh_token') ?? sessionStorage.getItem('refresh_token');

			// if (error.response?.status !== 401 && !originalRequest.headers.retry)
			return new Promise((resolve, reject) => {
				var userName = localStorage.getItem('user_name') ?? sessionStorage.getItem('user_name');

				if (refreshToken == null) {
					history.push('/Login?returnUrl=' + window.location.pathname);
					reject(error);
				}

				apiClient
					.post('auth/login/refresh', {
						refreshToken: refreshToken,
						userName: userName,
					})
					.then((response) => {
						let res: IAuthResponse = response.data;

						localStorage.setItem('access_token', res.data.accessToken);

						if (sessionStorage.getItem('refresh_token')) {
							sessionStorage.setItem('refresh_token', res.data.refreshToken);
						}
						if (localStorage.getItem('refresh_token')) {
							localStorage.setItem('refresh_token', res.data.refreshToken);
						}

						let userData = apiClient.getUserDataFromAccessToken();
						if (userData !== null) {
							apiClient.onLogin(userData);
						}

						//calling original request after token refresh
						if (originalRequest) {
							originalRequest.headers.retry = 'true';
							resolve(apiClient.request(originalRequest));
						}
					})
					.catch((error) => {
						if (window.location.pathname?.toLowerCase() !== routes.login) {
							history.push('/Login?returnUrl=' + window.location.pathname);
						}
						reject(error);
					});
			});
		} catch (error) {
			return new Promise((_resolve, reject) => {
				reject(error);
			});
		} finally {
			Helper.setLoader(false);
		}
	}
);

interface IApiClient extends AxiosInstance {
	baseURL: string;
	login(username: string, password: string, rememberMe: boolean): Promise<boolean>;
	userModuleLogin(sessionId: string, locationId: string | null): Promise<string>;
	logout(redirectToHomePage: boolean): void;
	getUserDataFromAccessToken(): IUserAuthData | null;
	onLogin: (authData: IUserAuthData) => void;
	onLogoff: () => void;
	resetPassword(token: string, tmpPassword: string, newPassword: string): Promise<boolean>;
	requestPasswordReset(email: string): Promise<boolean>;
	changePassword(password: string, newPassword: string): Promise<boolean>;
}

export interface ILoginRequest {
	username: string;
	password: string;
}

export interface IUserAuthData {
	id: string;
	name: string;
	userName: string;
	roles: string[];
	userWorkplaceId: string;
	availableFunctionalities: number;
}

// type of response from login endpoint
export interface IAuthResponse {
	data: {
		accessToken: string;
		refreshToken: string;
	};
}
